import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackException } from '../../telemetry/appInsights';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches React render/lifecycle errors and reports them to Application Insights.
 *
 * Note: `insertBefore` / DOM mismatch errors often come from third-party UI moving nodes
 * outside React’s tree (e.g. portals to `document.body`), browser extensions, or
 * StrictMode double-mounting. Keeping modals under `#root` reduces the risk.
 */
export class AppErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    trackException(error, {
      componentStack: errorInfo.componentStack ?? '',
      source: 'AppErrorBoundary',
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  public render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="bo-root">
          <main className="bo-main">
            <div className="bo-card" role="alert">
              <h1 style={{ marginTop: 0, fontSize: '1.25rem' }}>
                Something went wrong
              </h1>
              <p style={{ color: '#64748b' }}>
                The application hit an unexpected error. If this continues, try disabling
                browser extensions or contact support.
              </p>
              <pre
                style={{
                  background: '#f1f5f9',
                  padding: '0.75rem',
                  borderRadius: 8,
                  overflow: 'auto',
                  fontSize: '0.85rem',
                }}
              >
                {this.state.error.message}
              </pre>
              <div className="bo-actions" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="bo-btn bo-btn-primary"
                  onClick={this.handleRetry}
                >
                  Try again
                </button>
                <a className="bo-btn bo-btn-secondary" href="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
                  Go home
                </a>
              </div>
            </div>
          </main>
        </div>
      );
    }
    return this.props.children;
  }
}
